import { useEffect, useRef, useState } from 'react';
import type { Flashcard } from '@/types/Flashcard';
import { getString } from '@/utils/chatUtils/chatDefaultUtils';
import type { ChatScheduledVariant } from '@/services/vignettes';
import { fetchLinkedFlashcardsV5 } from '@/services/flashcards';
import { fetchScheduledVariantV5 } from '@/services/qbank';
import { isFutureScheduledVariant } from '@/utils/scheduledVariant';
import {
  isAnsweredCorrectly,
  toFlashcardToolItem,
  type ResolvedOption,
} from './questionItem';
import {
  BADGE_ADD_FAILED,
  type PendingBadge,
  type QuestionBadgeAddResult,
} from './questionBadgeAddResult';

interface UseMissedQuestionBadgesOptions {
  /** The persisted anchor's `vignette`, which seeds the scheduled variant. */
  vignetteAnchor: Record<string, unknown> | undefined;
  vignetteItemId: string;
  selected: string;
  answerConfirmed: boolean;
  options: ResolvedOption[];
  timezone: string | undefined;
  showStudyBadges: boolean;
  allowLinkedFlashcards: boolean;
  addBlocksUnavailableReason?: string;
  onOpenLinkedFlashcards?: (cards: Array<Record<string, unknown>>) => void;
  onAddLinkedFlashcardsBlock?: (
    flashcardIds: string[]
  ) => Promise<QuestionBadgeAddResult>;
  onAddScheduledVariantBlock?: (
    variantId: string
  ) => Promise<QuestionBadgeAddResult>;
}

/**
 * The "Scheduled 1 QBank Question" / "Assigned N Linked Flashcards" badges a
 * missed question shows, and the two modals behind them: their reads, their
 * adds to the conversation, and what each says while one is in flight or was
 * refused. `MissedQuestionBadges.tsx` draws what this returns.
 */
export const useMissedQuestionBadges = ({
  vignetteAnchor,
  vignetteItemId,
  selected,
  answerConfirmed,
  options,
  timezone,
  showStudyBadges,
  allowLinkedFlashcards,
  addBlocksUnavailableReason,
  onOpenLinkedFlashcards,
  onAddLinkedFlashcardsBlock,
  onAddScheduledVariantBlock,
}: UseMissedQuestionBadgesOptions) => {
  // Seed from the persisted anchor so the "Scheduled QBank Question" badge
  // survives a conversation reload — it's set after answering (below) and
  // persisted into the same anchor that restores `user_selected_letter`.
  const [scheduledVariant, setScheduledVariant] =
    useState<ChatScheduledVariant | null>(
      (vignetteAnchor?.scheduled_variant as ChatScheduledVariant | null) ?? null
    );

  const [scheduledPreviewModalIsOpen, setScheduledPreviewModalIsOpen] =
    useState(false);

  // The curation modal the flashcards badge now opens (ORA-832): the cards, a
  // switch each, and one "Study Now".
  const [linkedFlashcardsModalIsOpen, setLinkedFlashcardsModalIsOpen] =
    useState(false);

  // Bumped when that modal closes, so the badge's count reflects the curation
  // the student did inside it. The switches write through to their scheduling,
  // which is what the fetch below reads.
  const [linkedFlashcardsReadKey, setLinkedFlashcardsReadKey] = useState(0);

  // Whether a switch inside the modal is still writing. The read on close has to
  // follow the last write, or it can answer with the state the student just
  // changed — which the next open would then seed a switch from.
  const [linkedFlashcardWriteIsPending, setLinkedFlashcardWriteIsPending] =
    useState(false);

  // A close that happened while a write was in flight, waiting for it to settle.
  const rereadLinkedFlashcardsWhenSettled = useRef(false);

  // The vignette whose cards the student has curated behind the modal. The badge
  // is the only door back into it, so once they have been in there it stays even
  // if they turned every card off — otherwise unassign-all plus Close is a
  // two-tap one-way door out of their own review queue.
  const [curatedLinkedFlashcardsFor, setCuratedLinkedFlashcardsFor] = useState<
    string | null
  >(null);

  // The scheduled variant this badge was offering is gone (the server wrote
  // nothing and never will). The preview stays — it is what the student is
  // reading — but it stops offering to study it.
  const [scheduledVariantIsGone, setScheduledVariantIsGone] = useState(false);
  // The same for the cards: the server wrote nothing and never will, so the
  // modal keeps the list but stops offering to study it.
  const [linkedFlashcardsAreGone, setLinkedFlashcardsAreGone] = useState(false);

  // "Study Now" went back to a deck this mount had already made, rather than
  // minting one from the curation the student just changed. The modal says so
  // instead of scrolling away in silence.
  const [linkedFlashcardsAlreadyAdded, setLinkedFlashcardsAlreadyAdded] =
    useState(false);

  // Which vignette each on-demand read has already answered for. A set keeps
  // every question mounted and flips `showStudyBadges` as the student pages, so
  // without this every return to a missed question costs both reads again.
  const readLinkedFlashcardsFor = useRef<string | null>(null);
  const readScheduledVariantFor = useRef<string | null>(null);
  // A badge that adds to the conversation is a request: it says so while it is
  // in flight and says so again if it is refused, rather than looking inert.
  const [pendingBadge, setPendingBadge] = useState<PendingBadge | null>(null);

  // Which badge was refused, and what to say about it. Kept per badge because
  // each says it where its own student is looking: inside the modal that asked
  // while it is open, and on the badge row once it is closed.
  const [badgeError, setBadgeError] = useState<{
    badge: PendingBadge;
    message: string;
  } | null>(null);

  // Linked flashcards the backend scheduled when this question was missed. Not
  // persisted with the conversation — refetched (below) whenever there's a
  // confirmed wrong answer, so the badge/count always reflect live scheduling
  // state (a later correct re-answer would unassign them, dropping the badge).
  const [linkedFlashcards, setLinkedFlashcards] = useState<Flashcard[]>([]);

  // Fetch linked flashcards for a confirmed wrong answer — the same
  // `fetchLinkedFlashcardsV5` call QBank's SRBadge uses — so the badge can render
  // a count and its click can open a flashcard tool block. Also fires on load for
  // a wrong answer restored from the anchor, so the badge survives a reload
  // without persisting card content. A correct answer is skipped: the backend
  // unassigns its linked cards, so there'd be nothing to show.
  useEffect(() => {
    if (!answerConfirmed || !vignetteItemId || !selected) return;

    if (isAnsweredCorrectly(options, selected)) return;
    // Nothing would draw the count: a fetch for a badge that is kept out of
    // sight is a request for nothing (ORA-696), and the same is true of a badge
    // this student's exam has switched off.
    if (!showStudyBadges || !allowLinkedFlashcards) return;
    // Already read for this question, on an earlier visit to it.
    if (readLinkedFlashcardsFor.current === vignetteItemId) return;

    let cancelled = false;

    fetchLinkedFlashcardsV5(vignetteItemId)
      .then((cards) => {
        if (cancelled) return;

        readLinkedFlashcardsFor.current = vignetteItemId;
        setLinkedFlashcards(cards);
      })
      .catch(() => {
        // A failed fetch just leaves the badge hidden; the answer itself is
        // already recorded, so there's nothing to recover here.
      });

    return () => {
      cancelled = true;
    };
    // `item` is intentionally not a dep: it's stable for a given block, and
    // `vignetteItemId` already keys the effect to the vignette's identity.
  }, [
    allowLinkedFlashcards,
    answerConfirmed,
    linkedFlashcardsReadKey,
    selected,
    showStudyBadges,
    vignetteItemId,
  ]);

  // The re-read a close asked for while a switch was still writing (ORA-832).
  // Nothing cancels a priority write, so the read waits for it rather than
  // racing it: a GET that lands first would report the assignment the student
  // just turned off as still on.
  useEffect(() => {
    if (linkedFlashcardWriteIsPending) return;
    if (!rereadLinkedFlashcardsWhenSettled.current) return;

    rereadLinkedFlashcardsWhenSettled.current = false;
    readLinkedFlashcardsFor.current = null;
    setLinkedFlashcardsReadKey((key) => key + 1);
  }, [linkedFlashcardWriteIsPending]);

  // The variant the backend queued for a missed question, when the answer
  // response did not bring it (ORA-831). It rides back on the answer for the
  // question that was answered here, but nothing persists it for a question
  // inside a study set, and a reload of one has only the set's items to seed
  // from — so the badge asks for it instead, the same read QBank's block DTO
  // carries. A miss with nothing queued costs one cheap GET that answers null.
  useEffect(() => {
    if (!answerConfirmed || !vignetteItemId || !selected) return;
    if (isAnsweredCorrectly(options, selected)) return;
    if (!showStudyBadges) return;
    // Already known: the answer response, or the persisted anchor, had it.
    if (scheduledVariant) return;
    // Already read for this question; a stale one clears this to read again.
    if (readScheduledVariantFor.current === vignetteItemId) return;

    let cancelled = false;

    void fetchScheduledVariantV5(vignetteItemId).then((variant) => {
      if (cancelled) return;

      readScheduledVariantFor.current = vignetteItemId;
      if (variant) setScheduledVariant(variant);
    });

    return () => {
      cancelled = true;
    };
    // `options` is derived from `item`, which is stable for a given block; the
    // vignette's identity is what keys this effect, as above.
  }, [
    answerConfirmed,
    scheduledVariant,
    selected,
    showStudyBadges,
    vignetteItemId,
  ]);

  // Whether the user picked the correct option on this question. Mirrors how the
  // buttons and explanation resolve correctness above (`correct_letter` wins,
  // with each option's `is_correct` as the fallback), so the badges always agree
  // with what's shown on screen.
  const answeredCorrectly = isAnsweredCorrectly(options, selected);

  // Mirror the QBank SRBadge: only surface a scheduled variant that is still due
  // in the future (relative to the user's timezone) AND was actually missed. A
  // repeat attempt on an already-answered question is an idempotent no-op on the
  // backend that never recomputes scheduling, so it can echo a stale `DUE`
  // schedule even when this answer was correct — gate on correctness client-side
  // the way QBank does so a right answer never shows the "scheduled" badge.
  const hasFutureScheduledVariant = Boolean(
    scheduledVariant &&
    isFutureScheduledVariant(scheduledVariant.dueAt, timezone) &&
    !answeredCorrectly
  );

  // The linked flashcards the backend actually scheduled for this vignette (the
  // `isLinked` subset of the fetch). Mirrors QBank's SRBadge flashcard badge:
  // shown only for a missed question with at least one linked card. The click
  // opens those cards in a flashcard tool block instead of QBank's tab.
  const linkedFlashcardCards = linkedFlashcards.filter(
    (flashcard) => flashcard.isLinked
  );

  const linkedFlashcardCount = linkedFlashcardCards.length;

  // Whether the student has already curated this question's cards. QBank's rule
  // — a badge only for an assigned card — is what raises it, but once the modal
  // has been opened the badge is the way back in, so it survives a curation that
  // left nothing assigned (ORA-832).
  const hasCuratedLinkedFlashcards =
    Boolean(vignetteItemId) && curatedLinkedFlashcardsFor === vignetteItemId;

  const showLinkedFlashcardBadge =
    allowLinkedFlashcards &&
    Boolean(selected) &&
    !answeredCorrectly &&
    (linkedFlashcardCount > 0 ||
      (hasCuratedLinkedFlashcards && linkedFlashcards.length > 0));

  // What the badge says. The assigned count is the number that matters, so a
  // curation that turned everything off says so rather than counting the cards
  // the student just excluded.
  const linkedFlashcardBadgeLabel =
    linkedFlashcardCount > 0
      ? `Assigned ${linkedFlashcardCount} Linked Flashcard${
          linkedFlashcardCount === 1 ? '' : 's'
        }`
      : `Assigned 0 of ${linkedFlashcards.length} Linked Flashcard${
          linkedFlashcards.length === 1 ? '' : 's'
        }`;

  // The variant the badge is offering. The Companion adds exactly this one to
  // the conversation; the server checks it really is the student's next review.
  const scheduledVariantId = getString(scheduledVariant?.content?.id);

  /**
   * Run an add-to-the-conversation badge with a pending state and an inline
   * failure, the way the Sources pill's plus does. Only the Companion's
   * handlers go through here: a legacy handler just opens a tool block on the
   * spot, and "could not be added to the chat" would be a lie about it.
   */
  const runBadgeAction = async (
    badge: PendingBadge,
    run: () => Promise<QuestionBadgeAddResult>
  ): Promise<QuestionBadgeAddResult | null> => {
    if (pendingBadge) return null;

    setBadgeError(null);
    setLinkedFlashcardsAlreadyAdded(false);
    setPendingBadge(badge);

    try {
      const result = await run();

      if (result.ok) return result;

      setBadgeError({
        badge,
        message: result.message ?? BADGE_ADD_FAILED,
      });

      if (badge === 'scheduled_variant') {
        if (result.contentGone) setScheduledVariantIsGone(true);

        if (result.staleVariant) {
          // The preview was of a question the server no longer schedules, so it
          // closes and the badge reads what is scheduled now — which is what
          // makes the reason the student is shown true.
          readScheduledVariantFor.current = null;
          setScheduledVariant(null);
          setScheduledPreviewModalIsOpen(false);
        }
      } else if (result.contentGone) {
        setLinkedFlashcardsAreGone(true);
      }
    } catch {
      setBadgeError({ badge, message: BADGE_ADD_FAILED });
    } finally {
      setPendingBadge(null);
    }

    return null;
  };

  const canAddBlocks = !addBlocksUnavailableReason;

  const addsScheduledVariant = Boolean(
    onAddScheduledVariantBlock &&
    scheduledVariantId &&
    canAddBlocks &&
    !scheduledVariantIsGone
  );

  // The badge previews the cards, wherever they can be studied from here
  // (ORA-832): the modal is where the student sees what was assigned, turns off
  // what they do not want, and asks for the rest. A host that can only open a
  // local tool block keeps doing that on the click, as it always has.
  const openLinkedFlashcards = () => {
    if (addBlocksUnavailableReason) return;

    if (onAddLinkedFlashcardsBlock) {
      // Every card the read returned is curatable, assigned or not, so the modal
      // opens on anything it can draw. A refusal already on screen is left
      // standing: it is about the cards in this list, and nothing has changed
      // yet (ORA-832).
      if (!linkedFlashcards.length) return;

      setCuratedLinkedFlashcardsFor(vignetteItemId);
      setLinkedFlashcardsModalIsOpen(true);
      return;
    }

    if (!linkedFlashcardCount) return;

    onOpenLinkedFlashcards?.(linkedFlashcardCards.map(toFlashcardToolItem));
  };

  /**
   * Close the curation modal and re-read the assignments, so the badge's count
   * is what the student left behind rather than what the miss assigned. A write
   * still in flight defers the read until it settles, so the read cannot answer
   * with the state the student just changed.
   *
   * The refusal, if there is one, stays: with the modal closed the badge row is
   * where it shows, and it is cleared by the next ask or the next change rather
   * than by closing the only place it was readable.
   */
  const closeLinkedFlashcards = () => {
    setLinkedFlashcardsModalIsOpen(false);

    if (linkedFlashcardWriteIsPending) {
      rereadLinkedFlashcardsWhenSettled.current = true;
      return;
    }

    readLinkedFlashcardsFor.current = null;
    setLinkedFlashcardsReadKey((key) => key + 1);
  };

  /**
   * Study the cards the modal left enabled. It closes once they have landed —
   * unless the deck was already in the thread, when it stays open to say so:
   * the student re-curated and asked again, and a silent scroll to the deck
   * minted from the old selection would look like the change was honoured.
   */
  const studyLinkedFlashcardsNow = (flashcardIds: string[]) => {
    if (!onAddLinkedFlashcardsBlock) return;

    void runBadgeAction('flashcards', () =>
      onAddLinkedFlashcardsBlock(flashcardIds)
    ).then((result) => {
      if (!result?.ok) return;

      if (result.alreadyAdded) {
        setLinkedFlashcardsAlreadyAdded(true);
        return;
      }

      closeLinkedFlashcards();
    });
  };

  // The badge previews the variant, always (ORA-831). A surface that can study
  // it here offers that from the modal's "Study Now" — the student sees what
  // they are about to be given before it arrives in the thread — and one that
  // cannot (the exam screen) keeps the preview it always had.
  const openScheduledVariant = () => {
    if (addBlocksUnavailableReason) return;

    setBadgeError(null);
    setScheduledPreviewModalIsOpen(true);
  };

  /** Study the previewed variant here; the modal closes once it has landed. */
  const studyScheduledVariantNow = () => {
    if (!onAddScheduledVariantBlock || !scheduledVariantId) return;

    void runBadgeAction('scheduled_variant', () =>
      onAddScheduledVariantBlock(scheduledVariantId)
    ).then((result) => {
      if (result?.ok) setScheduledPreviewModalIsOpen(false);
    });
  };

  return {
    addBlocksUnavailableReason,
    addsScheduledVariant,
    badgeError,
    closeLinkedFlashcards,
    hasFutureScheduledVariant,
    linkedFlashcardBadgeLabel,
    linkedFlashcards,
    linkedFlashcardsAlreadyAdded,
    linkedFlashcardsAreGone,
    linkedFlashcardsModalIsOpen,
    // A switch the student moved is a new selection, so whatever the last
    // ask was told about the old one no longer applies.
    onCurationChanged: () => {
      setBadgeError(null);
      setLinkedFlashcardsAlreadyAdded(false);
    },
    openLinkedFlashcards,
    openScheduledVariant,
    pendingBadge,
    scheduledPreviewModalIsOpen,
    scheduledVariant,
    setLinkedFlashcardWriteIsPending,
    setScheduledPreviewModalIsOpen,
    setScheduledVariant,
    showLinkedFlashcardBadge,
    showStudyBadges,
    studyLinkedFlashcardsNow,
    studyScheduledVariantNow,
  };
};

export type MissedQuestionBadges = ReturnType<typeof useMissedQuestionBadges>;
